import { PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef } from 'react';

type SiteSearchSubmitHandler = (query: string) => void;

type SiteSearchSubmitContextValue = {
  registerSiteSearchSubmitHandler: (handler: SiteSearchSubmitHandler) => () => void;
  submitSiteSearchQuery: (query: string) => boolean;
};

const defaultContextValue: SiteSearchSubmitContextValue = {
  registerSiteSearchSubmitHandler: () => () => {},
  submitSiteSearchQuery: () => false,
};

const SiteSearchSubmitContext = createContext<SiteSearchSubmitContextValue>(defaultContextValue);

export const SiteSearchSubmitProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const submitHandlerRef = useRef<SiteSearchSubmitHandler | null>(null);

  const registerSiteSearchSubmitHandler = useCallback((handler: SiteSearchSubmitHandler) => {
    submitHandlerRef.current = handler;

    return () => {
      if (submitHandlerRef.current === handler) {
        submitHandlerRef.current = null;
      }
    };
  }, []);

  const submitSiteSearchQuery = useCallback((query: string) => {
    const submitHandler = submitHandlerRef.current;

    if (!submitHandler) {
      return false;
    }

    submitHandler(query);
    return true;
  }, []);

  const value = useMemo(
    () => ({
      registerSiteSearchSubmitHandler,
      submitSiteSearchQuery,
    }),
    [registerSiteSearchSubmitHandler, submitSiteSearchQuery]
  );

  return (
    <SiteSearchSubmitContext.Provider value={value}>{children}</SiteSearchSubmitContext.Provider>
  );
};

export const useSiteSearchSubmitContext = () => useContext(SiteSearchSubmitContext);
